import { Injectable, OnDestroy } from '@angular/core';
import { EfficiencyAnalysisApiService } from '@app/shared/services/api/efficiency-analysis-api.service';
import { MeterDataService } from '@app/shared/services/api/meter-data.service';
import moment from 'moment';
import { EfficiencyAnalysisColorStatus, EnergyTypesName } from '@app/shared/enums';
import { EfficiencyTresholdService } from '@app/efficiency-analysis/services/efficiency-treshold.service';
import * as d3 from 'd3';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { ElectricityMeterSelectionService } from './electricity-meter-selection/electricity-meter-selection.service';
import { isNil } from 'lodash';
import { StringUtility } from '@app/shared/string-utility';
import { ExtendedDateFormat, ISODateTimeStartOfDayFormat, consumerKomKeys } from '@app/shared/constants';
import { Observable, Subject } from 'rxjs';
import { MeterView, MeterViewResponse } from '@app/shared/models/meter-data/meter-view';
import { IRawWizardData } from '@app/efficiency-analysis/models/raw-wizard-data';
import { IRawHeatData } from '@app/efficiency-analysis/models/raw-heat-data';
import { EfficiencyThreshold } from '@app/efficiency-analysis/models/thresholds/efficiency-threshold';
import { takeUntil } from 'rxjs/operators';
import { ComparisonValues } from '@app/efficiency-analysis/models/comparison-values';
import { CalculatedEfficiency } from '@app/efficiency-analysis/models/heat-electricity-calculated-efficiency';
import { IHeatData } from '@app/efficiency-analysis/models/heat-data';
import { IBuildingEfficiencyData } from '@app/efficiency-analysis/models/buildings/building-efficiency-data';
import { ClimateFactorsDto } from '@app/shared/models/efficiency-analysis/climate-factors';
import { DateUtilities } from '@app/shared/date-utility';

@Injectable({
  providedIn: 'root',
})
export class BuildingEfficiencyWizardService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  public calculatedEfficiency?: CalculatedEfficiency;
  public comparisonValues?: ComparisonValues;
  public isPeriodChanged = false;

  private computedValues: Record<string, number> = {
    electricity: 0,
    heat: 0,
  };
  private efficient = 0;
  private electricityConsumption: number = 0;
  private inefficient = 0;

  constructor(
    private _efficiencyAnalysisService: EfficiencyAnalysisService,
    private efficiencyAnalysisApi: EfficiencyAnalysisApiService,
    private efficiencyTresholdService: EfficiencyTresholdService,
    private electricityMeterSelection: ElectricityMeterSelectionService,
    private meterData: MeterDataService
  ) {
    this.efficiencyTresholdService.treshold.subscribe((threshold: EfficiencyThreshold) => {
      this.efficient = threshold.efficientTreshold;
      this.inefficient = threshold.inefficientTreshold;
    });

    this.electricityMeterSelection.totalConsumption
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((consumption: number) => (this.electricityConsumption = consumption));
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public hasTimePeriod(buildingEfficiencyData: IBuildingEfficiencyData): boolean {
    return isNil(buildingEfficiencyData.efficiencyAnalysisStartDate) && isNil(buildingEfficiencyData.efficiencyAnalysisEndDate)
      ? false
      : DateUtilities.toExpectedFormat(buildingEfficiencyData.efficiencyAnalysisStartDate, ExtendedDateFormat) !==
          moment().subtract(2, 'years').startOf('year').format(ExtendedDateFormat);
  }

  public getConsumption(meter: MeterView, startDate: Date, endDate: Date): Observable<number> {
    const isStrom = StringUtility.areStringsIdentical(meter.energyTypesName, EnergyTypesName.electricity);
    const isConsumer = StringUtility.hasAnyMatchInArray(consumerKomKeys, [meter.komKey]);
    return this.efficiencyAnalysisApi.getConsumption(meter.guid, meter.meterTypeCode, meter.deviceEUI, isStrom, isConsumer, startDate, endDate);
  }

  public getMetersByMelo(melo: string, municipalityKey: string): Observable<MeterViewResponse> {
    return this.meterData.getMetersByMelo(melo, false, municipalityKey);
  }

  public calculateEfficiency(input: IRawWizardData, heatInput: IRawHeatData): void {
    this.calculateElectricity(input);
    this.calculateHeat(heatInput);

    const electricityPercentage = this.getEfficiencyPercentage(
      this.computedValues.electricity,
      this.comparisonValues?.colorElectricityMin as number,
      this.comparisonValues?.colorElectricityMax as number
    );
    const heatPercentage = this.getEfficiencyPercentage(
      this.computedValues.heat,
      this.comparisonValues?.colorHeatMin as number,
      this.comparisonValues?.colorHeatMax as number
    );
    this.calculatedEfficiency = {
      electricity: {
        average: this.comparisonValues?.characteristicValueElectricity as number,
        colorStatus: this.getEAColorStatusTriColor(electricityPercentage),
        consumption: this.computedValues.electricity,
        guideValue: this.comparisonValues?.referenceValueElectricity as number,
        maxRange: this.comparisonValues?.colorElectricityMax as number,
        minRange: this.comparisonValues?.colorElectricityMin as number,
        percentage: electricityPercentage,
      },
      heat: {
        average: this.comparisonValues?.characteristicValueHeat as number,
        colorStatus: this.getEAColorStatusTriColor(heatPercentage),
        consumption: this.computedValues.heat,
        guideValue: this.comparisonValues?.referenceValueHeat as number,
        maxRange: this.comparisonValues?.colorHeatMax as number,
        minRange: this.comparisonValues?.colorHeatMin as number,
        percentage: heatPercentage,
      },
    };
  }

  private calculateHeat(input: IRawHeatData): void {
    const floorArea = input.heatData[0].floorArea as number;
    const heatInputs = input.heatData
      .filter(
        (heat: IHeatData) =>
          // TODO: verify if intended data type is undefined or nullable
          !isNaN(heat.totalConsumption) || heat.totalConsumption !== 0 || !isNil(heat.totalConsumption)
      )
      .map((data: IHeatData) => data);
    const heatConsumptions: number[] = [];

    for (const heatInput of heatInputs) {
      const consumption = heatInput.totalConsumption && heatInput.calorificValueHi ? heatInput.totalConsumption / heatInput.calorificValueHi : 0;
      const calorificValueHi = heatInput.calorificValueHi as number;
      const climateFactor = heatInput.startDate ? this.getClimateFactor(heatInput.startDate) : 0;
      const powerConsumption = consumption * calorificValueHi;
      const energyConsumption = powerConsumption * climateFactor;
      const heatConsumption = energyConsumption / (floorArea * 0.9);
      heatConsumptions.push(heatConsumption);
    }

    let heatSum = heatConsumptions.reduce((acc: number, consumption: number) => acc + consumption, 0);

    if (heatSum === Infinity) {
      heatSum = 0;
    }
    this.computedValues.heat = parseFloat(heatSum.toFixed(2));
  }

  private calculateElectricity(input: IRawWizardData): void {
    const startDate = input.hasTimePeriod ? input.userDefinedStartDate : input.defaultStartDate;
    const endDate = input.hasTimePeriod ? input.userDefinedEndDate : input.defaultEndDate;
    const floorArea = input.buildingEfficiencyData?.floorArea;

    const totalConsumption = this.electricityConsumption;

    const period = Math.abs(moment(startDate).diff(moment(endDate), 'days'));
    const energyConsumption = totalConsumption * (365 / (period + 1));
    let powerConsumption = energyConsumption / (floorArea * 0.9);
    if (powerConsumption === Infinity) {
      powerConsumption = 0;
    }
    this.computedValues.electricity = parseFloat(powerConsumption.toFixed(2));
  }

  private getClimateFactor(startDate: Date): number {
    const refDate = moment(startDate);
    const start = moment(startDate).startOf('month');
    const end = moment(startDate).endOf('month');

    const a = refDate.diff(start, 'days');
    const b = end.diff(refDate, 'days');

    let searchDate = '';
    if (a <= b) {
      searchDate = moment(start).format(ISODateTimeStartOfDayFormat);
    } else {
      searchDate = moment(end).add(1, 'day').format(ISODateTimeStartOfDayFormat);
    }

    const isDateSimilar = (expected: Date, selected: string | Date): boolean => {
      return String(expected) === DateUtilities.toExpectedFormat(selected, ISODateTimeStartOfDayFormat);
    };

    const result = this._efficiencyAnalysisService.climateFactors.find((resp: ClimateFactorsDto) => isDateSimilar(resp.startDate, searchDate));

    if (result === undefined) {
      const oldestDate = this._efficiencyAnalysisService.climateFactors.reduce((m, v, i) => (v.startDate < m.startDate && i ? v : m)).startDate;
      const latestDate = this._efficiencyAnalysisService.climateFactors.reduce((m, v, i) => (v.startDate > m.startDate && i ? v : m)).startDate;

      const x = moment(searchDate).diff(moment(oldestDate), 'days');
      const y = moment(latestDate).diff(moment(searchDate), 'days');

      if (Math.abs(x) <= y) {
        return this._efficiencyAnalysisService.climateFactors.find((resp: ClimateFactorsDto) => isDateSimilar(resp.startDate, oldestDate))?.factor as number;
      } else {
        return this._efficiencyAnalysisService.climateFactors.find((resp: ClimateFactorsDto) => isDateSimilar(resp.startDate, latestDate))?.factor as number;
      }
    } else {
      return result.factor;
    }
  }

  getEAColorStatusTriColor(efficiencyPercentage: number): EfficiencyAnalysisColorStatus {
    const green = EfficiencyAnalysisColorStatus.efficient;
    const yellow = EfficiencyAnalysisColorStatus.midEfficient;
    const red = EfficiencyAnalysisColorStatus.inefficient;

    const efficient = this.efficient;
    const inefficient = this.inefficient;

    if (efficiencyPercentage >= 0 && efficiencyPercentage <= efficient) {
      return green;
    } else if (efficient < efficiencyPercentage && efficiencyPercentage < inefficient) {
      return yellow;
    }
    // ((inefficient <= efficiencyPercentage && efficiencyPercentage <= 100) || (efficiencyPercentage > 100))
    return red;
  }

  private getEfficiencyPercentage(calculatedEfficiency: number, minRange: number, maxRange: number): number {
    try {
      const xScale = d3.scaleLinear().domain([minRange, maxRange]).range([0, 100]);
      const result = xScale(calculatedEfficiency);
      return result > 100 ? 100 : parseInt(result.toFixed(0), 10);
    } catch {
      return 0;
    }
  }
}
